import {Utils} from '../../utils/utils';
import {BaseModel} from '../base';
import {context} from '../../constants/context';
import {question} from '../../services/question';
import {bbTimeZone} from '../../utils/time_zone';

class QuestionModel extends BaseModel {

    constructor(data) {
        // For some reason question is not currently initialised as a HAL object.
        super(data);

        if (this.price) {
            this.price = parseFloat(this.price);
        }

        if (this._data.default) {
            this.answer = this._data.default;
        }

        if (this._data.options) {
            for (const option of Array.from(this._data.options)) {
                if (option.is_default) {
                    this.answer = option.name;
                }

                if (this.hasPrice()) {
                    option.price = parseFloat(option.price);
                    const currency = data.currency_code ? data.currency_code : 'GBP';
                    //option.display_name = `${option.name} (${$filter('currency')(option.price, currency)})`; //TODO !!!
                    option.display_name = `${option.name} (${option.price} TODO )`;
                } else {
                    option.display_name = option.name;
                }
            }
        }

        if (this._data.detail_type === 'check' || this._data.detail_type === 'check-price') {
            this.answer = this._data.default && this._data.default === '1';
        }

        this.currentlyShown = this._data.admin_only ? context.isAdmin : true;
    }

    /**
     * @description
     * Check if it contains one of the following: "check-price", "select-price", "radio-price"
     *
     * @returns {boolean} If this contains detail_type
     */
    hasPrice() {
        return (this.detail_type === 'check-price') || (this.detail_type === 'select-price') || (this.detail_type === 'radio-price');
    }

    /**
     * @description
     * Select price if detail type si equal with check-price
     *
     * @returns {float} The returned selected price
     */
    selectedPrice() {
        if (!this.hasPrice()) {
            return 0;
        }
        if (this.detail_type === 'check-price') {
            return (this.answer ? this.price : 0);
        }
        for (const option of Array.from(this._data.options)) {
            if (this.answer === option.name) {
                return option.price;
            }
        }
        return 0;
    }

    /**
     * @description
     * Select price quantity if selected price has been selected
     *
     * @returns {object} The returned selected price quantity
     */
    selectedPriceQty(qty) {
        if (!qty) {
            qty = 1;
        }
        let p = this.selectedPrice();
        if (this.price_per_booking) {
            p = p * qty;
        }
        return p;
    }

    /**
     * @description
     * Show element
     *
     * @returns {boolean} If element is displayed
     */
    showElement() {
        this.currentlyShown = this.admin_only ? context.isAdmin : true;
    }

    /**
     * @description
     * Hide element
     *
     * @returns {boolean} If element is hidden
     */
    hideElement() {
        this.currentlyShown = false;
    }

    /**
     * @description
     * Get post data
     *
     * @returns {object} The returned post data
     */
    getPostData() {
        const data = Utils.subset(this, 'id', 'answer');

        // Sadly backend interprets 'false' answer as checked state. Answer has to be undefined for backend to
        // understand that the price wasn't selected.
        if (this.detail_type === 'check' || this.detail_type === 'check-price') {
            if (data.answer === false) {
                delete data.answer;
            }
        }

        if (this.detail_type === 'date' && this.answer) {
            data.answer = moment(this.answer).format('YYYY-MM-DD');
        }

        const price = this.selectedPrice();
        if (price) {
            data.price = price;
        }

        return data;
    }

    static $addAnswersByName(obj, keys) {
        return question.addAnswersByName(obj, keys);
    }

    static $addDynamicAnswersByName(questions) {
        return question.addDynamicAnswersByName(questions);
    }

    static $addAnswersFromDefaults(questions, answers) {
        return question.addAnswersFromDefaults(questions, answers);
    }

    updateConditionalQuestions() {
        if (this.conditionalQuestions) {
            this.conditionalQuestions.forEach((question) => {
                let conditionMet = false;

                // Checkbox is handled differently.
                if (this.detail_type === 'check') {
                    // In case of checkbox, condition is met when checkbox is not checked and there is no condition
                    // specified (empty conditional answer '{}') or when checkbox is checked and we have something
                    // passed as a conditional answer (it seems it doesn't really matter what).
                    conditionMet = !this.answer === Utils.isEmptyObject(question.settings.conditional_answers);
                } else {
                    // Applies to dropdown lists nd radio buttons.
                    conditionMet =
                        Object.keys(question.settings.conditional_answers).some((optionId) => {
                            // We need to find chosen option first. Condition is met if any of the conditional
                            // options matches actual answer.
                            return (this.getAnswerId() || '').toString() === optionId;
                        });
                }

                if (conditionMet) {
                    question.showElement();
                } else {
                    question.hideElement();
                }

                // For nested conditional questions.
                question.updateConditionalQuestions();
            });
        }
    }

    /**
     * @description
     * Retrieves answer id (valid for radio buttons and dropdown lists).
     */
    getAnswerId() {
        if (this.answer && this.options && this.options.length) {
            // Trimming is needed here as input fields may trim answers by their own resulting in undesired behaviour.
            return this.options.find(option => option.name.trim() === this.answer.trim()).id;
        }

    }

}

export {
    QuestionModel
};
